import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { Package, ArrowLeft, ShoppingCart, Tag, ChevronLeft, Star, TrendingUp, Shield, Award, Info, Plus, Minus, Image as ImageIcon } from 'lucide-react';
import { useCart } from '../../../contexts/CartContext';
import { toast } from '../../../components/ui/use-toast';
import { Toaster } from '../../../components/ui/toaster';

interface Product {
  id: string;
  name: string;
  description: string;
  stock: number;
  sku: string;
  ean: string;
  proPlusPrice: number;
  regularPrice: number;
  salePrice: number;
  mainImage?: string;
  galleryImages?: string[];
  images?: string[];
  category: string | null;
  isPrivateSale: boolean;
  companyId: string;
  isProPlus: boolean;
  promotionalPeriod?: {
    startDate: string;
    endDate: string;
  };
}

interface Company {
  id: string;
  commercialName: string;
  legalName: string;
}

interface Category {
  id: string;
  name: string;
  type?: string;
}

export default function PrivateSaleDetails() {
  const { id } = useParams();
  const [product, setProduct] = useState<Product | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [category, setCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { addItem } = useCart();

  useEffect(() => {
    if (!currentUser) {
      navigate('/dashboard/login');
      return;
    }
    loadData();
  }, [currentUser, id]);

  useEffect(() => {
    if (product) {
      // Definir a primeira imagem disponível como selecionada
      if (product.mainImage) {
        setSelectedImage(product.mainImage);
      } else if (product.images && product.images.length > 0) {
        setSelectedImage(product.images[0]);
      } else if (product.galleryImages && product.galleryImages.length > 0) {
        setSelectedImage(product.galleryImages[0]);
      }
    }
  }, [product]);

  const loadData = async () => {
    if (!id) return;

    try {
      const productDoc = await getDoc(doc(db, 'products', id));
      if (!productDoc.exists()) {
        setError('Produto não encontrado');
        return;
      }

      const productData = { id: productDoc.id, ...productDoc.data() } as Product;
      setProduct(productData);

      // Load company data
      const companyDoc = await getDoc(doc(db, 'companies', productData.companyId));
      if (companyDoc.exists()) {
        setCompany({ id: companyDoc.id, ...companyDoc.data() } as Company);
      }

      // Load category data if product has a category
      if (productData.category) {
        const categoryDoc = await getDoc(doc(db, 'categories', productData.category));
        if (categoryDoc.exists()) {
          setCategory({ id: categoryDoc.id, ...categoryDoc.data() } as Category);
        }
      }

      setError('');
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Erro ao carregar produto. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const isPromotionalPriceActive = (product: Product): boolean => {
    if (!product.promotionalPeriod?.startDate || !product.promotionalPeriod?.endDate) {
      return false;
    }

    const now = new Date().getTime();
    const startDate = new Date(product.promotionalPeriod.startDate).getTime();
    const endDate = new Date(product.promotionalPeriod.endDate).getTime();

    return now >= startDate && now <= endDate && product.salePrice > 0;
  };

  const getCurrentPrice = (product: Product) => {
    if (!product) return 0;

    // Se o produto tem preço ClubePro, SEMPRE usar esse preço
    if (product.isProPlus && product.proPlusPrice && product.proPlusPrice > 0) {
      console.log('Usando preço ClubePro:', product.proPlusPrice);
      return product.proPlusPrice;
    }
    
    // Caso NÃO tenha ClubePro, verificar se há preço promocional ativo
    if (isPromotionalPriceActive(product)) {
      console.log('Usando preço promocional:', product.salePrice);
      return product.salePrice;
    }
    
    // Se não houver promoção nem ClubePro, usar preço regular
    console.log('Usando preço regular:', product.regularPrice);
    return product.regularPrice;
  };

  const getDiscountedPrice = (basePrice: number, quantity: number) => {
    if (quantity >= 5) {
      return basePrice * 0.95; // 5% desconto
    }
    return basePrice;
  };

  const getFinalPrice = (product: Product, quantity: number) => {
    const basePrice = getCurrentPrice(product);
    return getDiscountedPrice(basePrice, quantity);
  };

  const handleAddToCart = () => {
    if (!product || product.stock <= 0 || quantity > product.stock) {
      toast({
        title: "Erro",
        description: product?.stock <= 0 
          ? "Este produto não tem stock disponível" 
          : "Quantidade solicitada excede o stock disponível",
        variant: "destructive",
      });
      return;
    }

    const price = getFinalPrice(product, quantity);
    if (price <= 0) {
      toast({
        title: "Erro",
        description: "Preço inválido para este produto",
        variant: "destructive",
      });
      return;
    }

    addItem({
      id: product.id,
      name: product.name,
      price: getFinalPrice(product, quantity),
      quantity: quantity,
      image: product.mainImage || '',
      sku: product.sku,
      stock: product.stock
    });

    toast({
      title: "Produto adicionado",
      description: `${quantity}x ${product.name} adicionado ao carrinho`,
    });
  };

  // Função para remover tags HTML e formatar o texto
  const formatDescription = (description: string) => {
    if (!description) return '';
    
    // Remove todas as tags HTML mas preserva quebras de linha
    const cleanText = description
      .replace(/<\/?[^>]+(>|$)/g, '') // Remove tags HTML
      .replace(/&quot;/g, '"')        // Converte &quot; em "
      .replace(/&amp;/g, '&')         // Converte &amp; em &
      .replace(/&lt;/g, '<')          // Converte &lt; em <
      .replace(/&gt;/g, '>')          // Converte &gt; em >
      .replace(/\s+/g, ' ')           // Remove espaços extras
      .trim();                        // Remove espaços no início e fim

    return cleanText;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <>
        <div className="p-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {error}
              </h2>
              <button
                onClick={() => navigate('/dashboard/drop')}
                className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
              >
                <ChevronLeft className="mr-2 h-5 w-5" />
                Voltar para produtos
              </button>
            </div>
          </div>
        </div>
        <Toaster />
      </>
    );
  }

  const companyName = company?.commercialName || company?.legalName || 'Empresa não encontrada';

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        {/* Header com navegação */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <button
              onClick={() => navigate('/dashboard/drop')}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-primary transition-colors font-medium"
            >
              <ChevronLeft size={20} />
              <span>Voltar ao Catálogo</span>
            </button>
          </div>
        </div>

        {/* Conteúdo Principal */}
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Galeria de Imagens */}
            <div className="space-y-4">
              {/* Imagem Principal */}
              <div className="aspect-square bg-white rounded-2xl shadow-sm border border-gray-200 flex items-center justify-center overflow-hidden">
                {selectedImage ? (
                  <img 
                    src={selectedImage} 
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="text-center">
                    <ImageIcon size={80} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500 text-lg font-medium">Sem imagem disponível</p>
                  </div>
                )}
              </div>

              {/* Miniaturas das Imagens Adicionais */}
              {(() => {
                // Combinar todas as imagens disponíveis, removendo duplicatas
                const allImages = [];
                if (product.mainImage) allImages.push(product.mainImage);
                if (product.galleryImages) allImages.push(...product.galleryImages);
                if (product.images) allImages.push(...product.images);
                
                // Remover duplicatas
                const uniqueImages = [...new Set(allImages)];
                
                return uniqueImages.length > 1 ? (
                  <div className="grid grid-cols-4 gap-2">
                    {uniqueImages.map((image: string, index: number) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImage(image)}
                        className={`aspect-square bg-white rounded-lg border-2 overflow-hidden transition-all ${
                          selectedImage === image 
                            ? 'border-primary shadow-md' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <img 
                          src={image} 
                          alt={`${product.name} - ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                ) : null;
              })()}

              {/* Descrição do Produto */}
              {product.description && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Info size={16} className="text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Descrição do Produto</h3>
                  </div>
                  <div className="prose prose-gray max-w-none">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {product.description}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Informações do Produto */}
            <div className="space-y-8">
              {/* Cabeçalho do Produto */}
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h1 className="text-3xl font-bold text-gray-900 leading-tight mb-3">{product.name}</h1>
                    
                    {/* Badges */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {product.isProPlus && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900 text-sm font-medium rounded-full">
                          <Award size={14} />
                          AreaPro
                        </span>
                      )}
                      {category && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full">
                          <Tag size={14} />
                          {category.name}
                        </span>
                      )}
                      {isPromotionalPriceActive(product) && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 text-sm font-medium rounded-full">
                          <TrendingUp size={14} />
                          Promoção
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Informações Técnicas */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações Técnicas</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {product.ean && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                          <Shield size={20} className="text-gray-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">EAN</p>
                          <p className="text-sm text-gray-600">{product.ean}</p>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                        <Package size={20} className="text-gray-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Stock</p>
                        <p className="text-sm text-gray-600">{product.stock} unidades</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Preços */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Informação de Preços</h3>
                
                <div className="space-y-4">
                  {/* Preço AreaPro */}
                  {product.isProPlus && product.proPlusPrice && product.proPlusPrice > 0 && (
                    <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-200 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center">
                            <Award size={20} className="text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-yellow-800">Preço AreaPro</p>
                            <p className="text-xs text-yellow-600">Preço de revenda</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-yellow-800">
                            {product.proPlusPrice.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* PVPR */}
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-600 rounded-lg flex items-center justify-center">
                          <TrendingUp size={20} className="text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800">PVPR</p>
                          <p className="text-xs text-gray-600">Preço de Venda ao Público Recomendado</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-800">
                          {isPromotionalPriceActive(product) ? (
                            <>
                              <span className="line-through text-lg text-gray-500 mr-2">
                                {product.regularPrice.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })}
                              </span>
                              {product.salePrice.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })}
                            </>
                          ) : (
                            product.regularPrice.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Lucro Potencial */}
                  {product.isProPlus && product.proPlusPrice && (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                            <Star size={20} className="text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-green-800">Margem de Lucro</p>
                            <p className="text-xs text-green-600">Vendendo ao PVPR</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-green-700">
                            {(
                              (isPromotionalPriceActive(product) ? product.salePrice : product.regularPrice) - 
                              product.proPlusPrice
                            ).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })}
                          </p>
                          <p className="text-sm text-green-600">
                            {(((isPromotionalPriceActive(product) ? product.salePrice : product.regularPrice) - product.proPlusPrice) / product.proPlusPrice * 100).toFixed(1)}% margem
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Controle de Quantidade */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quantidade</h3>
                
                {/* Destaque do Desconto */}
                {quantity >= 5 && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">%</span>
                      </div>
                      <p className="text-sm font-medium text-green-800">
                        🎉 Desconto de 5% aplicado! (5+ unidades)
                      </p>
                    </div>
                    <div className="mt-2 text-xs text-green-700">
                      Preço unitário: {getCurrentPrice(product).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })} → {getDiscountedPrice(getCurrentPrice(product), quantity).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })}
                    </div>
                  </div>
                )}
                
                {/* Aviso do Desconto (quando ainda não atingiu 5 unidades) */}
                {quantity < 5 && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">i</span>
                      </div>
                      <p className="text-sm text-blue-800">
                        💡 Compre 5+ unidades e ganhe 5% de desconto!
                      </p>
                    </div>
                    <div className="mt-1 text-xs text-blue-700">
                      Faltam {5 - quantity} unidades para o desconto
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-12 h-12 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition-colors"
                    disabled={quantity <= 1}
                  >
                    <Minus size={20} className={quantity <= 1 ? "text-gray-400" : "text-gray-700"} />
                  </button>
                  
                  <div className="text-center">
                    <span className="text-3xl font-bold text-gray-900">{quantity}</span>
                    <p className="text-sm text-gray-500 mt-1">
                      {product.stock > 0 ? `${product.stock} em stock` : 'Sem stock'}
                    </p>
                  </div>
                  
                  <button
                    onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                    className="w-12 h-12 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition-colors"
                    disabled={quantity >= product.stock}
                  >
                    <Plus size={20} className={quantity >= product.stock ? "text-gray-400" : "text-gray-700"} />
                  </button>
                </div>
                
                {quantity >= product.stock && product.stock > 0 && (
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800 text-center">
                      ⚠️ Quantidade máxima disponível: {product.stock}
                    </p>
                  </div>
                )}
              </div>

              {/* Compra */}
              <button
                onClick={handleAddToCart}
                disabled={product.stock <= 0 || quantity > product.stock}
                className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-200 flex items-center justify-center gap-3 ${
                  product.stock <= 0 || quantity > product.stock
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-primary to-primary-dark text-white hover:shadow-lg'
                }`}
              >
                <ShoppingCart size={24} />
                {product.stock <= 0 ? 'Sem Stock' : 'Adicionar ao Carrinho'}
                {product.stock > 0 && (
                  <span className={`ml-2 px-3 py-1 rounded-lg text-sm ${
                    product.stock <= 0 || quantity > product.stock
                      ? 'bg-gray-200 text-gray-500'
                      : 'bg-white/20'
                  }`}>
                    {quantity >= 5 && (
                      <span className="text-xs mr-1">(-5%)</span>
                    )}
                    {(getFinalPrice(product, quantity) * quantity).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <Toaster />
    </>
  );
}
